import { useAppSelector } from "app/hooks";
import Section from "components/Section";
import Thumbnail, { ThumbnailMedia } from "./Thumbnail";
import { useMemo, useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, styled, Typography } from "@mui/material";
import UploadIcon from "@mui/icons-material/UploadRounded";
import SlideShowModal from "features/slideShow/SlideShowModal";
import UploadFile from "./UploadFile";
import { CheckBox, CheckBoxOutlineBlank, IndeterminateCheckBox } from "@mui/icons-material";
import { selectUserRole } from "features/auth/authSlice";
import { UserRole } from "features/auth/types/user";
import { useGetThumbnailsByIdsQuery } from "app/api/media";
import { useChecklistContext } from "context/ChecklistContext";
import { useExcludePhotosFromPdfMutation } from "app/api/checklists";
import EmptyMessage from "components/EmptyMessage";

const MediaGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
}));

const MediaSection: React.FC = () => {
  const { checklist } = useChecklistContext();
  const verifications = checklist.verification;
  const { data: imageThumbUrls } = useGetThumbnailsByIdsQuery({
    thumbnailIds: verifications.map((v) => v.id),
  });
  const userRole = useAppSelector(selectUserRole);
  const [excludePhotosFromPdf] = useExcludePhotosFromPdfMutation();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);

  const userCanUpload = useMemo(() => {
    if (!userRole) return false;
    return [UserRole.ADMIN, UserRole.USER_ADMIN, UserRole.SALES].includes(userRole);
  }, [userRole]);

  const thumbnails: ThumbnailMedia[] = useMemo(() => {
    if (!imageThumbUrls || !verifications) return [];

    let thumbnails: ThumbnailMedia[];
    try {
      thumbnails = imageThumbUrls.map((url) => {
        const verification = verifications.find((v) => v.id === url.id);
        if (!verification) {
          throw new Error(`Verification not found for: ${url.id}`);
        }

        return { ...url, ...verification };
      });
    } catch (error) {
      console.error(error);
      return [];
    }
    return thumbnails;
  }, [imageThumbUrls, verifications]);

  const numberOfIncludedImages = useMemo(() => {
    return thumbnails.filter((t) => t.type === 1 && !t.excludedFromPdf).length;
  }, [thumbnails]);

  const isNoneIncluded = useMemo(() => {
    return thumbnails.every((t) => t.type === 2 || t.excludedFromPdf);
  }, [thumbnails]);

  const isAllIncluded = useMemo(() => {
    return thumbnails.every((t) => t.type === 2 || !t.excludedFromPdf);
  }, [thumbnails]);

  const isSomeIncluded = !isNoneIncluded && !isAllIncluded;

  const toggleAllIncluded = () => {
    const photoIds = thumbnails.filter((t) => t.type === 1).map((t) => t.id);
    excludePhotosFromPdf({
      ids: photoIds,
      excluded: !isNoneIncluded,
      checklistId: checklist.id,
    });
  };

  return (
    <Section p={1}>
      {!!thumbnails?.length && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormControlLabel
            labelPlacement="end"
            control={
              <Checkbox
                checked={isAllIncluded}
                disabled={!userCanUpload}
                indeterminate={isSomeIncluded}
                icon={<CheckBoxOutlineBlank />}
                indeterminateIcon={<IndeterminateCheckBox />}
                checkedIcon={<CheckBox />}
                onClick={toggleAllIncluded}
              />
            }
            label="Inkludera alla"
          />

          <Typography>
            <Box component={"span"} sx={{ fontWeight: "bold", fontSize: "large" }}>
              {numberOfIncludedImages} / {thumbnails.length}
            </Box>{" "}
            inkluderade
          </Typography>
        </Box>
      )}

      {thumbnails?.length > 0 ? (
        <MediaGrid>
          {thumbnails?.map((thumbnail, index) => (
            <Thumbnail
              key={thumbnail.id}
              data={thumbnail}
              onClick={() => {
                setSelectedIndex(index);
                setIsModalOpen(true);
              }}
            />
          ))}

          {isModalOpen && (
            <SlideShowModal
              onCloseModal={() => setIsModalOpen(false)}
              initialSlideIndex={selectedIndex}
              nameHidden={true}
              verifications={thumbnails}
            />
          )}
        </MediaGrid>
      ) : (
        <EmptyMessage m={1}>Inga tillagda</EmptyMessage>
      )}

      {userCanUpload && (
        <Box display="flex" justifyContent="end" mt={1}>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setShowUploadFile(true)}
          >
            Ladda upp
          </Button>
        </Box>
      )}

      {showUploadFile && <UploadFile onCloseModal={() => setShowUploadFile(false)} />}
    </Section>
  );
};

export default MediaSection;
